import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowRight } from 'lucide-react';

const EYB_LOGO = 'https://customer-assets.emergentagent.com/job_e62fe2c4-7b06-4359-9dcf-9bd67772605b/artifacts/k7n3nl06_Screenshot%202024-07-25%20103325.png';

const Welcome = () => {
  const [businessName, setBusinessName] = useState('');
  const navigate = useNavigate();

  const handleStart = () => {
    if (businessName.trim()) {
      localStorage.setItem('eybBusinessName', businessName.trim());
      navigate('/journey');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && businessName.trim()) {
      handleStart();
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-mesh">
      {/* Layered animated background */}
      <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none"></div>
      <div className="absolute top-20 left-10 w-80 h-80 rounded-full blur-3xl animate-pulse-slow" style={{ background: 'radial-gradient(circle, rgba(91,160,208,0.35), transparent 70%)' }}></div>
      <div className="absolute bottom-20 right-10 w-[28rem] h-[28rem] rounded-full blur-3xl animate-pulse-slow" style={{ background: 'radial-gradient(circle, rgba(0,51,102,0.28), transparent 70%)', animationDelay: '1s' }}></div>
      <div className="absolute top-1/2 left-1/3 w-64 h-64 rounded-full blur-3xl animate-drift" style={{ background: 'radial-gradient(circle, rgba(173,216,230,0.35), transparent 70%)' }}></div>

      {/* Floating sparkles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(12)].map((_, i) => (
          <span
            key={i}
            className="spark"
            style={{
              left: `${(i * 83) % 100}%`,
              top: `${(i * 47) % 100}%`,
              animationDelay: `${(i * 0.7) % 5}s`,
              animationDuration: `${6 + (i % 5)}s`,
            }}
          />
        ))}
      </div>

      {/* Main content */}
      <div className="min-h-screen flex items-center justify-center px-4 relative z-10">
        <div className="max-w-2xl w-full">
          <div className="text-center space-y-8">
            {/* Bouncing EYB logo */}
            <div className="flex justify-center animate-fadeInUp">
              <div className="relative group" data-testid="eyb-logo-hero">
                <div className="absolute inset-0 rounded-full blur-3xl opacity-60 animate-glow" style={{ background: 'radial-gradient(circle, #4FC3F7, #29ABE2)' }}></div>
                <img
                  src={EYB_LOGO}
                  alt="EYB Consulting"
                  className="relative h-32 w-auto select-none animate-float group-hover:scale-105 smooth-transition"
                  style={{
                    WebkitMaskImage: 'radial-gradient(circle at 50% 50%, #000 45%, transparent 47%)',
                    maskImage: 'radial-gradient(circle at 50% 50%, #000 45%, transparent 47%)'
                  }}
                  draggable="false"
                />
              </div>
            </div>

            {/* Headline */}
            <h1
              className="text-5xl md:text-6xl font-semibold leading-tight animate-fadeInUp tracking-tight"
              style={{ color: '#0B2545', animationDelay: '0.2s', opacity: 0, animationFillMode: 'forwards' }}
            >
              Every business has a story.
              <br />
              <span className="gradient-text-animated">
                Let's explore yours.
              </span>
            </h1>

            {/* Body text */}
            <p
              className="text-xl text-gray-600 leading-relaxed animate-fadeInUp max-w-xl mx-auto"
              style={{ animationDelay: '0.4s', opacity: 0, animationFillMode: 'forwards' }}
            >
              Enter your business name below and see how EYB helps businesses grow — from startup to strategic leadership.
            </p>

            {/* Input section */}
            <div
              className="max-w-md mx-auto space-y-6 animate-fadeInUp"
              style={{ animationDelay: '0.6s', opacity: 0, animationFillMode: 'forwards' }}
            >
              <div className="space-y-3">
                <Label htmlFor="businessName" className="text-left block text-lg font-medium" style={{ color: '#0B2545' }}>
                  Your Business Name
                </Label>
                <div className="relative group">
                  <div className="absolute -inset-0.5 rounded-md bg-gradient-to-r from-sky-400 via-blue-500 to-indigo-500 opacity-0 group-focus-within:opacity-70 blur smooth-transition"></div>
                  <Input
                    id="businessName"
                    data-testid="business-name-input"
                    type="text"
                    placeholder="e.g., Bean & Co."
                    value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="relative h-14 text-lg border-2 focus:ring-4 focus:ring-sky-200 smooth-transition bg-white"
                    style={{ borderColor: '#29ABE2' }}
                  />
                </div>
              </div>

              <Button
                onClick={handleStart}
                disabled={!businessName.trim()}
                data-testid="start-journey-btn"
                className="w-full h-14 text-base font-bold group relative overflow-hidden smooth-transition tracking-wide"
                style={{
                  background: businessName.trim() ? '#0B2545' : '#9ca3af',
                  color: '#ffffff',
                  borderRadius: '9999px',
                  opacity: businessName.trim() ? 1 : 0.6,
                  boxShadow: businessName.trim() ? '0 8px 24px -8px rgba(11, 37, 69, 0.5)' : 'none'
                }}
              >
                <span className="relative z-10 flex items-center justify-center gap-2">
                  Start the Journey
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 smooth-transition" />
                </span>
                {businessName.trim() && (
                  <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></span>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Welcome;
